import { byPrefixAndName } from '@awesome.me/kit-5c288c4b3f/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { graphql, Link, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'
import { Button } from './Button'
import { ContentfulProductsProps } from '../types'

const Footer = () => {
  const data = useStaticQuery(graphql`
    query {
      soc: contentfulAsset(file: { fileName: { eq: "SOC_NonCPA_Blk.png" } }) {
        title
        gatsbyImageData(layout: FULL_WIDTH)
      }
      dv01Logo: contentfulAsset(
        contentful_id: { eq: "1A6cMInS2mBWi5IMerlNoF" }
      ) {
        id
        title
        file {
          url
        }
      }
      contentfulMainDescription(name: { eq: "Footer Address" }) {
        text {
          raw
        }
      }
      allContentfulProducts(sort: { order: ASC }) {
        nodes {
          id
          name
          slug
        }
      }
    }
  `)

  return (
    <div className="bg-gray-1000 pb-50 pt-12">
      <div className="container mx-auto px-4 sm:px-0">
        <footer className="grid items-stretch gap-4 md:grid-cols-12 md:gap-7">
          <div className="col-span-6 col-start-1 row-start-1 flex flex-col items-start justify-between md:col-span-3 md:col-start-1">
            <div className="text-xxs text-gray-50">
              <object
                className="pointer-events-none inline-flex h-16"
                type="image/svg+xml"
                title={data.dv01Logo.title}
                data={data.dv01Logo.file.url}
              >
                <span className="sr-only">{data.dv01Logo.title}</span>
              </object>
              {data.contentfulMainDescription.text &&
                renderRichText(data.contentfulMainDescription.text)}
            </div>
            <div className="flex">
              <a
                className="mr-m inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-dark text-md text-gray-50"
                target="_blank"
                rel="noreferrer noopener"
                href="https://www.linkedin.com/company/dv01-investortech-for-lending-market-transparency/"
              >
                <span className="sr-only">LinkedIn</span>
                <FontAwesomeIcon icon={byPrefixAndName.fab['linkedin-in']} />
              </a>
              <a
                className="mr-m inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-dark text-md text-gray-50"
                target="_blank"
                rel="noreferrer noopener"
                href="https://twitter.com/dv01inc"
              >
                <span className="sr-only">Twitter</span>
                <FontAwesomeIcon icon={byPrefixAndName.fab['twitter']} />
              </a>
              <a
                className="mr-m inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-dark text-md text-gray-50"
                target="_blank"
                rel="noreferrer noopener"
                href="https://www.instagram.com/dv01inc/"
              >
                <span className="sr-only">Instagram</span>
                <FontAwesomeIcon icon={byPrefixAndName.fab['instagram']} />
              </a>
            </div>
          </div>
          <div className="col-span-12 col-start-1 row-start-3 md:col-span-3 md:col-start-4 md:row-start-1">
            <h5 className="text-xs leading-loose text-gray-500">Offerings</h5>
            <ul className="list-none">
              {data.allContentfulProducts.nodes.map(
                (product: ContentfulProductsProps) => (
                  <li key={product.id}>
                    <Link
                      className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                      to={`/offerings/${product.slug}/`}
                    >
                      {product.name}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>
          <div className="col-span-12 col-start-1 row-start-4 md:col-span-3 md:col-start-7 md:row-start-1">
            <h5 className="text-xs leading-loose text-gray-500">Resources</h5>
            <ul className="list-none">
              <li>
                <Link
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  to="/resources/blog/"
                >
                  Blog
                </Link>
              </li>
              <li>
                <a
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  href="https://dv01.freshdesk.com/support/home"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Documentation & Guides
                </a>
              </li>
            </ul>
          </div>
          <div className="col-span-12 col-start-1 row-start-5 md:col-span-3 md:col-start-10 md:row-start-1">
            <h5 className="text-xs leading-loose text-gray-500">Company</h5>
            <ul className="list-none">
              <li>
                <Link
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  to="/company/about-us/"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  to="/company/careers/"
                >
                  Careers
                </Link>
              </li>
              <li>
                <Link
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  to="/contact/"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  to="/privacy-policy/"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  className="h-6 py-2 text-xs leading-loose text-gray-50 hover:text-primary"
                  to="/terms-and-conditions/"
                >
                  Terms and Conditions
                </Link>
              </li>
            </ul>
          </div>
          <div className="col-span-12 col-start-1 row-start-2 md:col-span-3 md:col-start-1">
            <h5 className="text-xs leading-loose text-gray-500">
              Subscribe to Updates
            </h5>
            <p className="text-xs leading-normal text-gray-50">
              Be the first to know about product launches, exclusive insights
              reports, and industry news.
            </p>
          </div>
          <div className="col-span-6 col-start-7 row-start-1 md:col-span-3 md:col-start-10 md:row-start-2">
            <a
              href="https://resources.dv01.co/dv01-becomes-soc-2-type-2-compliant/"
              rel="canonical"
            >
              <GatsbyImage
                className="w-24"
                image={data.soc.gatsbyImageData}
                alt="SOC 2 Type 2 Compliant"
              />
            </a>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default Footer
